/**
 * Local Wikipedia-style lore article: Remnants of Humanity (faction).
 * Content is authored here — not loaded from the database.
 */
const remnantsOfHumanityArticle = {
  title: 'Remnants of Humanity',
  image: '/factions/remnants.png',
  format: 'wiki',
  text: `**Humanity** (scientific name: *Homo sapiens*) is an intelligent, highly social mammalian species originating from the planet **Earth** in the **Sol** system. Unlike many known starfaring civilizations, humanity did not emerge with significant natural biological advantages. Instead, it achieved planetary dominance through exceptional adaptability, technological innovation, cooperative behavior, and an unusual willingness to modify both its environment and itself.

Humans are regarded by many xenohistorians as one of the most expansionist and unpredictable intelligent species ever documented. Their history is characterized by rapid technological progress, frequent internal conflict, and an extraordinary ability to survive under adverse conditions.

---

## Origin

Humanity evolved naturally on Earth over hundreds of thousands of years.

Like all known terrestrial life, humans descend from simpler biological organisms through the process of evolution by natural selection. Anatomically modern humans first appeared in Africa and gradually spread across the planet, replacing or absorbing closely related hominid populations.

For the overwhelming majority of their existence, humans lived as hunter-gatherers in small tribal groups. Agriculture emerged only recently on a historical timescale and fundamentally transformed human civilization by allowing permanent settlements, population growth, and increasingly complex societies.

Within a few thousand years of developing agriculture, humans established cities, states, empires, and eventually a globally interconnected civilization.

---

## Biology

Humans are warm-blooded omnivorous mammals.

### Physical Characteristics

Compared to many apex predators, humans possess relatively modest physical abilities:

- Limited natural armor
- No significant natural weapons
- Average sensory perception
- Moderate speed and strength
- High endurance over long distances

Despite these apparent disadvantages, humans became Earth's dominant species through intelligence, tool use, and cooperation.

### Reproduction

Humans reproduce sexually and invest extraordinary resources into raising offspring.

Human children remain dependent for many years after birth, one of the longest developmental periods known among large terrestrial animals. This prolonged childhood enables extensive learning and cultural transmission.

### Lifespan

Historically human lifespan varied dramatically depending on living conditions.

Modern medicine has greatly increased average longevity, with individuals commonly living between seventy and one hundred years.

Unlike the **Exolots**, humans suffer from disease, aging, injury, and genetic disorders throughout their lives.

---

## Psychology

Human cognition is often described as a paradoxical combination of cooperation and competition.

### Social Intelligence

Humans possess a remarkable ability to understand intentions, emotions, and motivations in others.

This capability allowed the creation of:

- Families
- Tribes
- Nations
- Religions
- Corporations
- Scientific institutions
- Global political organizations

Humans routinely cooperate with millions of unrelated individuals whom they will never meet.

### Abstract Thought

One of humanity's defining characteristics is the ability to organize around abstract concepts that do not physically exist.

These include:

- Money
- Laws
- Nations
- Ideologies
- Moral systems
- Scientific theories

This trait enables extremely large-scale cooperation but also contributes to ideological conflict.

### Curiosity

Virtually every major stage of human development was driven by curiosity.

Humans repeatedly explored:

- Unknown territory
- Dangerous environments
- Deep oceans
- Polar regions
- Outer space

Often with little immediate practical benefit.

This behavior has been described by some observers as irrational but remains one of humanity's most effective evolutionary advantages.

---

## Civilization

Human civilization developed through successive periods of technological and social complexity.

### Early States

The first major human civilizations emerged in river valleys where agriculture could reliably support large populations.

Over time increasingly sophisticated societies appeared across the globe.

These civilizations developed:

- Writing
- Mathematics
- Engineering
- Astronomy
- Organized warfare
- Legal systems

### Scientific Revolution

A major turning point occurred when humans began systematically testing explanations against observable evidence.

The resulting scientific method dramatically accelerated technological development.

Within only a few centuries humanity progressed from wind-powered ships to industrial manufacturing, global telecommunications, nuclear energy, computers, and spaceflight.

### Digital Age

The creation of planetary communication networks transformed human society.

Information could now travel across the world almost instantly.

This allowed unprecedented economic integration, scientific collaboration, and cultural exchange, while also creating new forms of social and political instability.

---

## Warfare

Conflict has accompanied human civilization throughout its history.

Humans have repeatedly demonstrated both immense destructive capacity and extraordinary military innovation.

### Pre-Industrial Warfare

Early conflicts were fought using simple weapons such as:

- Spears
- Bows
- Swords
- Shields

Over time increasingly complex military organizations emerged.

### Industrial Warfare

The industrial era enabled warfare on previously unimaginable scales.

Mass production allowed nations to deploy millions of soldiers while producing vast quantities of weapons.

The twentieth century witnessed conflicts that caused tens of millions of deaths.

### Nuclear Weapons

Humanity's development of nuclear weapons represented a unique milestone.

For the first time a species acquired the ability to destroy its own civilization.

Paradoxically, this capability may also have reduced direct warfare between major powers due to the risk of mutual destruction.

---

## Exploration and Expansion

Humans display unusually strong expansionist tendencies.

Long before achieving spaceflight, they settled nearly every habitable region of Earth.

This included:

- Deserts
- Mountains
- Rainforests
- Arctic environments
- Isolated islands

The invention of spacecraft allowed humanity to leave its homeworld and begin exploring nearby celestial bodies.

Many historians consider space exploration a continuation of behavioral patterns already visible throughout earlier human history.

---

## Technology

Humanity's primary strength lies in technological adaptation.

Where many species evolve biological solutions, humans frequently engineer artificial ones.

Examples include:

| Biological Problem | Human Solution |
| --- | --- |
| Poor natural armor | Protective equipment |
| Limited strength | Machinery |
| Inability to fly | Aircraft |
| Weak senses | Sensors and instruments |
| Disease | Medicine |
| Hostile climates | Environmental engineering |

As a consequence humanity often changes faster culturally than biologically.

---

## Strengths

Humanity possesses several characteristics frequently cited by xenologists:

### Adaptability

Humans function effectively in a wide range of environmental and social conditions.

### Innovation

They continuously generate new technologies and methods of problem solving.

### Cooperation

Large-scale cooperation between unrelated individuals allows complex societies to function.

### Persistence

Humans are notable for continuing efforts despite repeated failures.

### Cultural Transmission

Knowledge accumulates across generations, allowing rapid progress without requiring biological evolution.

---

## Weaknesses

Humanity's greatest strengths often create corresponding vulnerabilities.

### Tribalism

Humans naturally divide themselves into competing groups.

Historical conflicts have frequently emerged from differences in identity, ideology, or resource competition.

### Misinformation

The same communication systems that enable cooperation can also spread false beliefs.

### Environmental Impact

Human industrial activity has repeatedly damaged ecosystems upon which civilization depends.

### Short-Term Thinking

Individual humans often prioritize immediate gains over long-term consequences.

---

## Legacy

Humanity's history is defined neither by peace nor conquest alone, but by continual transformation.

Within a few thousand years humans progressed from stone tools to digital networks, from isolated tribes to global civilization, and from observing the stars to reaching beyond their planet.

What makes humanity remarkable is not physical superiority nor intellectual perfection. Human history is filled with mistakes, wars, injustices, and self-inflicted crises. Yet the species repeatedly demonstrates an ability to learn, adapt, recover, and move forward.

Many historians therefore regard humanity not as the strongest species it could be, but as one uniquely defined by its refusal to remain what it was.

---

## Conclusion

Most extraterrestrial analysts agree on one conclusion: humanity's greatest asset is neither its intelligence nor its technology.

It is its capacity for change.

Throughout recorded history humans have repeatedly reinvented their societies, beliefs, economies, and technologies whenever existing systems became insufficient. This process is often chaotic, violent, and unpredictable, yet it has enabled the species to overcome challenges that would have destroyed many others.

For this reason humanity is often classified not as a stable civilization, but as an evolving one.

Its final form remains unknown.
`,
};

export default remnantsOfHumanityArticle;
